import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { authService } from "@/services/authService";
import { trackPageView } from "@/composables/useAnalytics";
import * as Sentry from "@sentry/capacitor";
import {
  buildDestinationFromRoute,
  getStoredReferralCode,
  recordReferralVisit,
  storeReferralCode,
} from "@/utils/referral";
import {
  clearChunkRecoveryPhase,
  isChunkLoadErrorMessage,
  recoverFromChunkLoadFailure,
} from "@/utils/chunkRecovery";
// Lazy load views
const PlaylistsView = () => import("@/views/PlaylistsView.vue");
const PlaylistDetailView = () => import("@/views/PlaylistDetailView.vue");
const LoginView = () => import("@/views/LoginView.vue");
const TimelineView = () => import("@/views/TimelineView.vue");
const DiscoverView = () => import("@/views/DiscoverView.vue");
const SearchView = () => import("@/views/SearchView.vue");
const GunLootView = () => import("@/views/GunLootView.vue");
const NotificationsView = () => import("@/views/NotificationsView.vue");
const BookmarksView = () => import("@/views/BookmarksView.vue");
const MessagesView = () => import("@/views/MessagesView.vue");
const SettingsView = () => import("@/views/SettingsView.vue");
const FeedbackView = () => import("@/views/FeedbackView.vue");
const ManagedUsersView = () => import("@/views/ManagedUsersView.vue");
const GroupsView = () => import("@/views/GroupsView.vue");
const GroupDetailView = () => import("@/views/GroupDetailView.vue");
const GroupFeedView = () => import("@/views/GroupFeedView.vue");
const GroupManagementView = () => import("@/views/GroupManagementView.vue");
const ProfileView = () => import("@/views/ProfileView.vue");
const AccountSetupView = () => import("@/views/AccountSetupView.vue");

// Detail views
const PostDetailView = () => import("@/views/PostDetailView.vue");

// User views
const BadgeCollectionView = () => import("@/views/BadgeCollectionView.vue");

// Admin views
const CEODashboardView = () => import("@/views/CEODashboardView.vue");

// Analytics views
const ContentAnalyticsView = () => import("@/views/ContentAnalyticsView.vue");

// PWA views
const ShareView = () => import("@/views/ShareView.vue");
const PwaTestView = () => import("@/views/PwaTestView.vue");

// Help views
const HelpSupportView = () => import("@/views/HelpSupportView.vue");

// OAuth views
const OAuthCallbackView = () => import("@/views/OAuthCallbackView.vue");


const routes: RouteRecordRaw[] = [
  // ============================================================
  // HOME ROUTE - Always redirects to timeline (social-only app)
  // ============================================================
  {
    path: "/",
    redirect: "/timeline",
  },

  // ============================================================
  // MAIN ROUTES
  // ============================================================
  {
    path: "/timeline/:scope(all|mystuff)?",
    name: "timeline",
    component: TimelineView,
  },
  {
    // Legacy redirect for old /discover/timeline URLs
    path: "/discover/timeline/:scope(all|mystuff)?",
    redirect: (to) => ({
      name: "timeline",
      params: { scope: to.params.scope || "all" },
      query: to.query,
    }),
  },
  {
    path: "/discover",
    name: "discover",
    component: DiscoverView,
    meta: {
      requiresAuth: false,
      showSidebar: true,
      showBottomNav: true,
    },
  },
  {
    path: "/discover/:category",
    name: "discover-category",
    component: TimelineView,
    meta: {
      requiresAuth: false,
      showSidebar: true,
      showBottomNav: true,
    },
  },

  // ============================================================
  // PLAYLIST ROUTES (video playlists)
  // ============================================================
  {
    path: "/playlists",
    name: "playlists",
    component: PlaylistsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/playlists/:ulid/:slug?",
    redirect: (to) => ({
      name: "playlist-detail",
      params: { ulid: to.params.ulid, slug: to.params.slug },
      query: to.query,
    }),
  },
  {
    path: "/playlists/discover",
    name: "playlists-discover",
    component: PlaylistsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/playlist/:ulid/:slug?",
    name: "playlist-detail",
    component: PlaylistDetailView,
    meta: {
      appHeader: { title: "Playlist", preferDetailChromeTitle: true },
    },
  },

  // ============================================================
  // LEGACY REDIRECTS - Support old prefixed routes
  // ============================================================
  // Music legacy routes → redirect to timeline
  { path: "/music", redirect: "/timeline" },
  { path: "/music/playlists", redirect: "/playlists" },
  { path: "/music/playlist/:id/:slug?", redirect: (to) => `/playlist/${to.params.id}${to.params.slug ? "/" + to.params.slug : ""}` },

  // Social legacy routes
  { path: "/social", redirect: "/timeline" },
  { path: "/social/all", redirect: "/timeline" },
  { path: "/social/groups", redirect: "/communities" },
  { path: "/social/groups/:id", redirect: (to) => `/communities/${to.params.id}` },
  { path: "/social/groups/:id/manage", redirect: (to) => `/communities/${to.params.id}/manage` },

  // Old /groups → /communities redirects
  { path: "/groups", redirect: "/communities" },
  { path: "/groups/:id", redirect: (to) => `/communities/${to.params.id}` },
  { path: "/groups/:id/feed", redirect: (to) => `/communities/${to.params.id}/feed` },
  { path: "/groups/:id/manage", redirect: (to) => `/communities/${to.params.id}/manage` },

  // Old route redirects
  { path: "/explore", redirect: "/timeline" },
  { path: "/trending", redirect: "/timeline" },
  { path: "/analytics", redirect: (to) => {
    const user = authService.getUser();
    return user?.username ? `/profile/${user.username}/analytics/overview` : "/";
  }},

  // ============================================================
  // COMMUNITY ROUTES (formerly /groups)
  // ============================================================
  {
    path: "/communities",
    name: "groups",
    component: GroupsView,
  },
  {
    path: "/communities/:id/feed",
    name: "group-feed",
    component: GroupFeedView,
  },
  {
    path: "/communities/:id",
    name: "group-detail",
    component: GroupDetailView,
    meta: {
      appHeader: { title: "Community", preferDetailChromeTitle: true },
    },
  },
  {
    path: "/communities/:id/manage",
    name: "group-manage",
    component: GroupManagementView,
    meta: {
      requiresAuth: true,
      appHeader: {
        title: "Group management",
        preferDetailChromeTitle: true,
      },
    },
  },

  // ============================================================
  // TEST/DEMO ROUTES
  // ============================================================
  // (Injected below in dev only)

  // ============================================================
  // UTILITY ROUTES
  // ============================================================
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { requiresGuest: true },
  },
  {
    path: "/auth/:provider/callback",
    name: "oauth-callback",
    component: OAuthCallbackView,
  },
  {
    path: "/setup",
    name: "setup",
    component: AccountSetupView,
    meta: { requiresAuth: true },
  },
  {
    path: "/search",
    name: "search",
    component: SearchView,
  },
  {
    path: "/gunloot",
    name: "gunloot",
    component: GunLootView,
    meta: {
      appHeader: { title: "GunLoot" },
    },
  },
  {
    path: "/share",
    name: "share",
    component: ShareView,
  },
  {
    path: "/pwa-test",
    name: "pwa-test",
    component: PwaTestView,
  },
  {
    path: "/notifications",
    name: "notifications",
    component: NotificationsView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Notifications" },
    },
  },
  {
    path: "/bookmarks",
    name: "bookmarks",
    component: BookmarksView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Bookmarks" },
    },
  },
  {
    path: "/messages",
    name: "messages",
    component: MessagesView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Messages" },
    },
  },
  {
    path: "/messages/:ulid",
    name: "conversation",
    component: MessagesView,
    meta: { requiresAuth: true },
  },
  {
    path: "/settings/feedback",
    name: "feedback",
    component: FeedbackView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Send Feedback" },
    },
  },
  {
    path: "/settings/:section?",
    name: "settings",
    component: SettingsView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Settings" },
    },
  },
  {
    path: "/help",
    name: "help",
    component: HelpSupportView,
    meta: { appHeader: { title: "Help & Support" } },
  },
  {
    path: "/manage-users",
    name: "manage-users",
    component: ManagedUsersView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Manage Users" },
    },
  },
  {
    path: "/badges",
    name: "badges",
    component: BadgeCollectionView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Badge Collection" },
    },
  },
  {
    path: "/admin/dashboard",
    name: "ceo-dashboard",
    component: CEODashboardView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "CEO Dashboard" },
    },
    beforeEnter: (_to, _from, next) => {
      const user = authService.getUser();
      const hasAdministratorBadge =
        user?.badges?.includes("administrator") ?? false;

      if (!hasAdministratorBadge) {
        next({ path: "/" });
        return;
      }

      next();
    },
  },

  // ============================================================
  // ANALYTICS ROUTES (Profile-based) — must be before profile catch-all
  // ============================================================
  {
    path: "/profile/:username/analytics",
    redirect: (to) => `/profile/${to.params.username}/analytics/overview`,
  },
  {
    path: "/profile/:username/analytics/overview",
    name: "analytics-overview",
    component: ContentAnalyticsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile/:username/analytics/performance",
    name: "analytics-performance",
    component: ContentAnalyticsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/music/analytics",
    redirect: () => {
      const user = authService.getUser();
      return user?.username ? `/profile/${user.username}/analytics/overview` : "/";
    },
  },

  // ============================================================
  // PROFILE ROUTES
  // ============================================================
  {
    path: "/profile/:username/settings",
    name: "profile-settings",
    component: SettingsView,
    meta: {
      requiresAuth: true,
      appHeader: { title: "Settings" },
    },
  },
  {
    path: "/profile/:username/:tab?",
    name: "profile",
    component: ProfileView,
    beforeEnter: (to, _from, next) => {
      // Default to feed tab if no tab specified
      if (!to.params.tab) {
        next({
          name: 'profile',
          params: { username: to.params.username, tab: 'feed' },
          replace: false,
        });
      } else {
        next();
      }
    },
  },

  // ============================================================
  // CONTENT DETAIL ROUTES
  // ============================================================
  {
    path: "/post/:ulid",
    name: "post-detail",
    component: PostDetailView,
    meta: {
      appHeader: { title: "Post Details", preferDetailChromeTitle: true },
    },
  },
  { path: "/posts/:ulid", redirect: (to) => ({ path: `/post/${to.params.ulid}`, query: to.query }) },
  // Legacy song/track redirects → post detail
  { path: "/song/:ulid", redirect: (to) => `/post/${to.params.ulid}` },
  { path: "/track/:ulid", redirect: (to) => `/post/${to.params.ulid}` },
  // Legacy video/short URLs → canonical post detail (same as /post/:ulid)
  {
    path: "/v/:ulid",
    redirect: (to) => ({
      path: `/post/${to.params.ulid}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/s/:ulid",
    redirect: (to) => ({
      path: `/post/${to.params.ulid}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  { path: "/podcast/:ulid", redirect: (to) => `/post/${to.params.ulid}` },
  {
    path: "/burst/:ulid",
    name: "burst-detail",
    component: PostDetailView,
    meta: {
      appHeader: { title: "Post Details", preferDetailChromeTitle: true },
    },
  },

  // ============================================================
  // CATCH-ALL
  // ============================================================
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
  {
    path: "/test/theme",
    name: "theme-test",
    component: () => import("@/views/ThemeTestView.vue"),
  },
];

// Keep test/demo views out of production bundles (especially important for iOS Safari perf).
// This also avoids shipping large mock-data screens and dev-only utilities.
if (import.meta.env.DEV) {
  routes.push(
    {
      path: "/dev/tools",
      name: "developer-tools",
      component: () => import("@/views/DeveloperToolsView.vue"),
      meta: { appHeader: { title: "Developer Tools" } },
    },
    {
      path: "/test/post/card",
      name: "postcard-test",
      component: () => import("@/views/PostCardTestView.vue"),
    },
  );
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Return false to disable automatic scroll restoration
    // We handle scroll position manually via useVirtualFeed composable
    return false;
  },
});

// If a route's component chunk fails to load (common after deployments when the
// client is still running an older build), navigation can appear "broken" until
// the user hard refreshes. Recover via cache-bust → plain URL → guarded reload.
router.onError((error, to) => {
  if (typeof window === "undefined") return;

  const message = String((error as any)?.message ?? error);

  // Log ALL router errors for debugging navigation issues
  console.error("[Router] Navigation error:", { message, to: to.fullPath, error });

  Sentry.withScope((scope) => {
    scope.setTag("error_source", "router");
    scope.setContext("router", {
      to: to.fullPath,
      message,
      isChunkLoadError: isChunkLoadErrorMessage(message),
    });
    Sentry.captureException(error);
  });

  if (!isChunkLoadErrorMessage(message)) return;

  recoverFromChunkLoadFailure(to.fullPath);
});

// Navigation watchdog - detects "stuck" navigation where URL changes but view doesn't update
// This can happen when chunk loading fails silently or component errors break reactivity
let navigationWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
let lastCompletedPath: string | null = null;

router.afterEach((to) => {
  clearChunkRecoveryPhase();

  if (typeof to.query._chunk_cb === "string") {
    const restQuery = { ...to.query };
    delete restQuery._chunk_cb;
    void router.replace({
      path: to.path,
      query: restQuery,
      hash: to.hash,
    });
    return;
  }

  // Clear any existing watchdog
  if (navigationWatchdogTimer) {
    clearTimeout(navigationWatchdogTimer);
    navigationWatchdogTimer = null;
  }

  // Track page view in Google Analytics (GA4)
  trackPageView(to.fullPath, (to.meta?.title as string) || undefined);

  // Track successful navigation
  lastCompletedPath = to.fullPath;

  // Set a watchdog that checks if the view actually rendered.
  // Uses 3s timeout to account for async data loading + route transition animations.
  // Checks multiple containers since single-post mode renders outside app-layout__center.
  navigationWatchdogTimer = setTimeout(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // Check if the current URL matches what we expect
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    if (currentPath !== lastCompletedPath) {
      return;
    }

    // Check multiple possible content containers (single-post mode uses different layout)
    const routerViewContent = document.querySelector(".app-layout__center");
    const hasRouterContent = routerViewContent && routerViewContent.children.length > 0;
    const hasFeedContent = !!document.querySelector(".feed-list, .single-post-feed, .profile-view, .search-view");
    const isTransitioning = !!document.querySelector(".page-fade-enter-active, .page-fade-leave-active");

    if (!hasRouterContent && !hasFeedContent && !isTransitioning) {
      console.warn("[Router] Navigation watchdog: View may be stuck - no content rendered after navigation to:", to.fullPath);
    }
  }, 3000);
});

// Disable browser's native scroll restoration
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// Navigation guard for auth
router.beforeEach(async (to, _from, next) => {
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getUser();
  const referralCode =
    typeof to.query.ref === "string" ? to.query.ref : getStoredReferralCode();
  const destination = buildDestinationFromRoute(to);

  if (referralCode) {
    storeReferralCode(referralCode);
    void recordReferralVisit({
      referralCode,
      destinationType: destination.destinationType,
      destinationId: destination.destinationId,
      landingPath: to.fullPath,
    });
  }

  const isReferralLanding = Boolean(destination.destinationType);

  // Content detail pages can be viewed without authentication
  // even with referral codes - we'll show a signup prompt modal instead
  const isContentDetailPage = ["post-detail", "burst-detail"].includes(
    to.name as string,
  );

  if (
    !isAuthenticated &&
    referralCode &&
    isReferralLanding &&
    !isContentDetailPage &&
    to.name !== "login"
  ) {
    next({
      name: "login",
      query: { redirect: to.fullPath, ref: referralCode },
    });
    return;
  }

  // Routes that require authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({
      name: "login",
      query: { redirect: to.fullPath, ...(referralCode ? { ref: referralCode } : {}) },
    });
    return;
  }

  // Routes that require guest (login page)
  if (to.meta.requiresGuest && isAuthenticated) {
    next("/");
    return;
  }

  // Check if user needs to complete account setup
  if (
    isAuthenticated &&
    user &&
    user.accountSetup === false &&
    to.name !== "setup"
  ) {
    next({ name: "setup" });
    return;
  }

  // Redirect away from setup if already completed
  if (
    to.name === "setup" &&
    isAuthenticated &&
    user &&
    user.accountSetup !== false
  ) {
    next("/");
    return;
  }

  next();
});

export default router;
